import type { LessonCreditSummary, PaymentSummary, StudentLesson } from "./api.js";
import { describePayment } from "./policy.js";

export type StudentPaymentBadgeTone = "neutral" | "warning";

export interface StudentPaymentBadge {
  label: string;
  tone: StudentPaymentBadgeTone;
}

/** Translate the current booking state without showing storage enum values. */
export function studentBookingStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: "Awaiting tutor confirmation",
    approved: "Confirmed",
    canceled: "Cancelled",
    cancelled: "Cancelled",
    rejected: "Declined",
    completed: "Completed",
    no_show: "Missed",
  };
  return labels[status.toLowerCase()] ?? "Status unavailable";
}

/** The credit read is already evaluated by the booking API. Match its eligible
 * tutor and service to this lesson, and only describe unsettled lessons this way.
 * This does not apply a credit or change the payment state. */
export function studentLessonHasAvailableCredit(
  lesson: StudentLesson,
  credits: LessonCreditSummary | null | undefined,
  studentId: string,
): boolean {
  if (credits?.portal_student_id !== studentId || !lesson.enrichment) return false;
  if (lesson.domain === "school" || lesson.unavailable) return false;
  if (lesson.enrichment.payment.state !== "pending") return false;
  if (["cancelled", "canceled", "inactive", "rejected", "completed"].includes(
    (lesson.enrichment.booking_status || lesson.status).toLowerCase(),
  )) return false;
  if (lesson.providerId == null || lesson.serviceId == null) return false;
  return credits.blocks.some(
    (block) =>
      block.provider_id === lesson.providerId &&
      block.service_id === lesson.serviceId &&
      block.available_credits > 0 &&
      block.booking.allowed,
  );
}

export function studentLessonPaymentDescription(
  lesson: StudentLesson,
  credits: LessonCreditSummary | null | undefined,
  studentId: string,
): string | null {
  if (!lesson.enrichment) return null;
  if (studentLessonHasAvailableCredit(lesson, credits, studentId)) return "Credits available";
  if (lesson.enrichment.payment.state === "not_required" &&
      ["lesson_credit", "coupon"].includes(lesson.enrichment.payment.method)) {
    return "Lesson credit used";
  }
  if (studentLessonPaysOnTheDay(lesson.enrichment.payment)) return "Pay on the day";
  return describePayment(lesson.enrichment.payment);
}

/** An upcoming on-site lesson is not overdue; say when it is paid, not that it is unpaid. */
function studentLessonPaysOnTheDay(payment: PaymentSummary): boolean {
  return payment.state === "pending" && payment.method === "on_site";
}

/**
 * Render FastAPI's payment state without collapsing distinct outcomes.
 *
 * A lesson-credit booking is settled even though FastAPI calls the money state
 * `not_required`; complimentary/no-charge lessons are different and stay
 * "No payment due". Missing enrichment returns no badge rather than guessing
 * that a raw mirror appointment is unpaid.
 */
export function studentPaymentBadge(
  payment: PaymentSummary | null | undefined,
  creditAvailable = false,
): StudentPaymentBadge | null {
  if (!payment) return null;
  if (creditAvailable && payment.state === "pending") {
    return { label: "Credits available", tone: "neutral" };
  }
  if (studentLessonPaysOnTheDay(payment)) return { label: "Pay on the day", tone: "neutral" };

  switch (payment.state) {
    case "paid":
      return { label: "Paid", tone: "neutral" };
    case "not_required":
      return payment.method === "lesson_credit" || payment.method === "coupon"
        ? { label: "Lesson credit used", tone: "neutral" }
        : { label: "No payment due", tone: "neutral" };
    case "pending":
    case "failed":
      return { label: "Unpaid", tone: "warning" };
    case "partially_paid":
      return { label: "Part paid", tone: "warning" };
    case "partially_refunded":
      return { label: "Part refunded", tone: "neutral" };
    case "refunded":
      return { label: "Refunded", tone: "neutral" };
    default:
      return { label: "Payment unknown", tone: "neutral" };
  }
}

export type StudentRecurrenceCadence = "weekly" | "fortnightly";

interface RecurrenceWithCadence {
  recurrence_plan_id: string | null;
  student_change_scope: "single_occurrence" | "not_recurring";
  cadence?: unknown;
}

/**
 * Cadence is an additive field in the paired WMF-SIte student-enrichment
 * contract. Validate it at runtime so the Hub can be deployed before or after
 * the backend change without treating an absent field as evidence of a plan.
 */
export function studentRecurrenceCadence(lesson: StudentLesson): StudentRecurrenceCadence | null {
  const recurrence = lesson.enrichment?.recurrence as RecurrenceWithCadence | undefined;
  if (!recurrence || recurrence.student_change_scope !== "single_occurrence") return null;
  if (!recurrence.recurrence_plan_id) return null;
  return recurrence.cadence === "weekly" || recurrence.cadence === "fortnightly"
    ? recurrence.cadence
    : null;
}

export interface StudentRecurringPlanSummary {
  planId: string;
  cadence: StudentRecurrenceCadence;
  serviceName: string | null;
  tutorName: string | null;
}

/**
 * Summarise authoritative recurrence contexts already attached to upcoming
 * lessons. Dates are deliberately ignored: two lessons seven/fourteen days
 * apart do not prove a recurrence plan exists.
 */
export function studentRecurringPlanSummaries(
  lessons: readonly StudentLesson[],
): StudentRecurringPlanSummary[] {
  const plans = new Map<string, StudentRecurringPlanSummary>();

  for (const lesson of lessons) {
    const cadence = studentRecurrenceCadence(lesson);
    const planId = lesson.enrichment?.recurrence.recurrence_plan_id ?? null;
    if (!cadence || !planId || plans.has(planId)) continue;
    plans.set(planId, {
      planId,
      cadence,
      serviceName: lesson.serviceName,
      tutorName: lesson.tutorName,
    });
  }

  return [...plans.values()];
}

export function studentRecurringPlanLabel(cadence: StudentRecurrenceCadence): string {
  return cadence === "weekly" ? "Weekly recurring lesson plan" : "Fortnightly recurring lesson plan";
}
